import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Hammer, Layers, Truck, ShieldCheck, Smile, BookOpen, Play, X } from "lucide-react";
import { SITE, IMAGES, CREDIBILITY, STUDIOS, DIRECTORS, WHAT_I_BRING, FEATURED_PROJECTS } from "../mock";
import Seo from "../components/Seo";
import { Dialog, DialogContent } from "../components/ui/dialog";

const BRING_ICONS = [Smile, Layers, Hammer, BookOpen, Truck, ShieldCheck];

// Convert a YouTube watch URL into an embed URL
const toEmbed = (url) => {
  if (!url) return "";
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0` : url;
};

const Home = () => {
  const [activeTrailer, setActiveTrailer] = useState(null);

  return (
    <div className="bg-[#0E0E0F] text-[#EDE3CB]">
      <Seo
        path="/"
        description="Jesse J. Adams — Los Angeles-based IATSE Local 44 Property Master. 15+ years on studio comedies, period dramas, streaming features, and specialty work. 48' property trailer + full kit. Available worldwide."
      />
      {/* HERO */}
      <section className="relative min-h-[70vh] md:min-h-[92vh] flex items-end overflow-hidden bg-[#0B0B0C]">
        <div className="absolute inset-0">
          <picture>
            <source media="(min-width: 768px)" srcSet={IMAGES.hero} />
            <img
              src={IMAGES.heroMobile}
              alt="Jesse J. Adams Property Master director's chair on a film set"
              className="w-full h-full object-cover object-[center_30%] md:object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0E0E0F]/25 to-[#0E0E0F]" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-12 md:py-16 w-full">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-8">
            <div className="max-w-3xl">
              <div className="text-xs md:text-sm tracking-[0.28em] md:tracking-[0.32em] uppercase text-[#B8935A] mb-3 md:mb-4">IATSE Local 44 · Los Angeles Based Property Master</div>
              <p className="font-serif-accent italic text-xl sm:text-2xl md:text-4xl text-[#EDE3CB] max-w-2xl leading-snug">
                Creative instincts, historical research, archive-level detail, and budget-minded department leadership for film and television.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/resume" className="btn-primary">View Resume <ArrowUpRight size={14} /></Link>
              <Link to="/contact" className="btn-secondary">Check Availability</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CREDIBILITY STRIP */}
      <section className="tickerstrip bg-[#0E0E0F]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-6 gap-x-4">
          {CREDIBILITY.map((c) => (
            <div key={c.label} className="flex flex-col">
              <div className="font-display text-2xl text-[#EDE3CB]">{c.value}</div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#EDE3CB]/55 mt-1">{c.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="section-label">Introduction</div>
          <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">From Prop PA to <span className="brass-text">Prop Master</span>.</h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-[#EDE3CB]/80 text-lg leading-relaxed">
          <p>
            I started in the property department in 2008 as a production assistant and researcher, then worked my way through every level of the department — prop assistant, Assistant Property Master, and ultimately Property Master. That path gave me a true ground-level understanding of the work: research, product placement sourcing, fabrication, continuity, budgeting, crew communication, vendor relationships, and wrap.
          </p>
          <p>
            Over more than 15 years in film and television, I've worked across comedy, drama, action, science fiction, period pieces, streaming projects, music videos, commercials, and major studio films. I was mentored by a veteran Property Master with 35+ years of experience, which shaped the way I approach every production: respect the craft, protect the budget, support the story, and lead a department people can rely on.
          </p>
          <Link to="/about" className="inline-flex items-center gap-2 text-[#B8935A] tracking-[0.22em] uppercase text-xs font-medium link-underline">
            Read full bio <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* STUDIO MARQUEE */}
      {/* DIRECTORS MARQUEE */}
      <section className="bg-[#0E0E0F] border-y border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-12">
          <div className="section-label mb-8 text-center">Directors I've Collaborated With</div>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 items-center">
            {DIRECTORS.map((d, i) => (
              <React.Fragment key={d}>
                <div className="font-serif-accent text-lg md:text-2xl text-[#EDE3CB]/85 hover:text-[#B8935A] transition-colors">
                  {d}
                </div>
                {i < DIRECTORS.length - 1 && (
                  <span className="text-[#B8935A]/50 text-sm hidden md:inline">·</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="section-label">Recent Projects</div>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">Recent <span className="brass-text">work</span>. <br />Click a poster to watch the trailer.</h2>
          </div>
          <Link to="/resume" className="btn-secondary self-start md:self-auto">All Credits <ArrowUpRight size={14} /></Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {FEATURED_PROJECTS.map((p) => (
            <button
              type="button"
              onClick={() => setActiveTrailer(p)}
              key={p.title}
              className="project-card-hover group block text-left w-full"
              aria-label={`Watch trailer for ${p.title}`}
              data-testid={`trailer-poster-${p.title.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="relative aspect-[2/3] overflow-hidden bg-[#0B0B0C] border border-white/5">
                {p.poster ? (
                  <img src={p.poster} alt={`${p.title} poster`} className="pc-img w-full h-full object-contain" />
                ) : (
                  <div className="absolute inset-0 flex flex-col justify-between p-5 bg-gradient-to-b from-[#141415] via-[#0E0E0F] to-[#0B0B0C] border-l border-r border-[#B8935A]/15">
                    <div>
                      <div className="text-[10px] tracking-[0.32em] uppercase text-[#B8935A]">{p.studio}</div>
                      <div className="brass-divider mt-3 opacity-60" />
                    </div>
                    <div>
                      <div className="font-display text-2xl md:text-3xl tracking-wide text-[#EDE3CB] leading-[0.95]">{p.title}</div>
                      <div className="text-[10px] tracking-[0.32em] uppercase text-[#EDE3CB]/55 mt-3">{p.year}</div>
                    </div>
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#EDE3CB]/40">Poster Coming Soon</div>
                  </div>
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-[#0E0E0F]/0 group-hover:bg-[#0E0E0F]/55 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-[#B8935A] flex items-center justify-center">
                      <Play size={22} className="text-[#0E0E0F] ml-1" fill="#0E0E0F" />
                    </div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#EDE3CB]">Watch Trailer</div>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-display text-lg md:text-xl tracking-wide group-hover:text-[#B8935A] transition-colors leading-tight">{p.title}</div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#B8935A] shrink-0">{p.year}</div>
                </div>
                <div className="text-[#EDE3CB]/65 text-[13px] mt-1">{p.studio} · {p.role}</div>
                {p.type && (
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#B8935A]/80 mt-1.5">{p.type}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* WHAT I BRING */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="section-label">What I Bring</div>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">Six things production <br />gets on day one.</h2>
          </div>
          <Link to="/services" className="btn-secondary self-start md:self-auto">The Prop Department <ArrowUpRight size={14} /></Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {WHAT_I_BRING.map((w, i) => {
            const Icon = BRING_ICONS[i] || Layers;
            return (
              <div key={w.title} className="bg-[#0E0E0F] p-8 hover:bg-[#141415] transition-colors duration-300">
                <Icon className="text-[#B8935A]" size={28} strokeWidth={1.4} />
                <div className="font-display text-2xl mt-6 tracking-wide">{w.title}</div>
                <p className="text-[#EDE3CB]/70 mt-3 leading-relaxed text-[15px]">{w.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* STUDIO MARQUEE */}
      <section className="border-y border-white/5 bg-[#0B0B0C]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-10">
          <div className="section-label mb-6 text-center">Studios I've Worked With</div>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 items-center">
            {STUDIOS.map((s) => (
              <div key={s} className="font-display text-lg md:text-xl tracking-[0.2em] text-[#EDE3CB]/55 hover:text-[#B8935A] transition-colors">
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRAILER FEATURE */}
      <section className="relative">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 relative">
              <img src={IMAGES.trailer} alt="48 foot property trailer" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute -bottom-5 left-5 bg-[#0E0E0F] border border-[#B8935A] px-5 py-3">
                <div className="section-label">Production-Ready</div>
                <div className="font-display text-2xl mt-1">48' Trailer + Full Kit</div>
              </div>
            </div>
            <div className="lg:col-span-5 space-y-6">
              <div className="section-label">Trailer + Kit</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95]">A department <br />on <span className="brass-text">day one</span>.</h2>
              <p className="text-[#EDE3CB]/75 text-lg leading-relaxed">
                A 48' property trailer and comprehensive kit available as part of my Property Master package. Organized storage, prep + wrap support, travel-ready infrastructure — fewer rentals, less cost to production. <span className="text-[#B8935A] font-medium">Rate fully negotiable regardless of budget!</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ARCHIVE FEATURE */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1 space-y-6">
            <div className="section-label">Archive Work</div>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.95]">Historical Document Replication for <br /><span className="brass-text">The Walt Disney Archives</span>.</h2>
            <p className="text-[#EDE3CB]/75 text-lg leading-relaxed">
              Historical document recreation including the Walt Disney Company founding contract and the Steamboat Willie production script.
              Plus inventory and cataloging of historical merchandise.
            </p>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 grid grid-cols-2 gap-4">
            <div className="aspect-[3/4] bg-[#0B0B0C] border border-white/5 overflow-hidden flex items-center justify-center">
              <img src={IMAGES.typewriter1} alt="Steamboat Willie production script — recreated for The Walt Disney Archives" className="w-full h-full object-contain" />
            </div>
            <div className="aspect-[3/4] bg-[#0B0B0C] border border-white/5 overflow-hidden flex items-center justify-center mt-10">
              <img src={IMAGES.typewriter2} alt="Walt Disney founding distribution contract — recreated for The Walt Disney Archives" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.bts} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0E0E0F]/85" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-28 text-center">
          <div className="section-label">Inquiries</div>
          <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95] max-w-4xl mx-auto">
            Available for features, series, additional photography units, commercials, music videos, prop fabrication, and archive-related projects.
          </h2>
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/contact" className="btn-primary">Check Availability <ArrowUpRight size={14} /></Link>
            <a href={`mailto:${SITE.email}`} className="btn-secondary">{SITE.email}</a>
          </div>
        </div>
      </section>

      {/* Trailer modal */}
      <Dialog open={!!activeTrailer} onOpenChange={(o) => !o && setActiveTrailer(null)}>
        <DialogContent className="max-w-5xl bg-[#0B0B0C] border-[#B8935A]/25 p-0 overflow-hidden" data-testid="trailer-modal">
          <button
            type="button"
            onClick={() => setActiveTrailer(null)}
            aria-label="Close trailer"
            className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full bg-[#0E0E0F]/90 hover:bg-[#B8935A] hover:text-[#0E0E0F] text-[#EDE3CB] flex items-center justify-center transition-colors"
          >
            <X size={18} />
          </button>
          {activeTrailer && (
            <div className="aspect-video w-full bg-black">
              <iframe
                src={toEmbed(activeTrailer.trailer)}
                title={`${activeTrailer.title} trailer`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}
          {activeTrailer && (
            <div className="px-6 py-4 border-t border-white/5 flex items-baseline justify-between gap-3">
              <div>
                <div className="font-display text-xl tracking-wide">{activeTrailer.title}</div>
                <div className="text-[11px] tracking-[0.28em] uppercase text-[#B8935A]/85 mt-1">
                  {activeTrailer.studio} · {activeTrailer.year}
                </div>
              </div>
              <a
                href={activeTrailer.trailer}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] tracking-[0.22em] uppercase text-[#EDE3CB]/70 hover:text-[#B8935A] inline-flex items-center gap-1"
              >
                Open on YouTube <ArrowUpRight size={12} />
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
